import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Loader2, Trash2 } from "lucide-react";
import SignatureCanvas from "react-signature-canvas";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  useAdminSignature,
  useSaveAdminSignature,
  useDeleteAdminSignature,
} from "@/hooks/useAdminSignature";
import {
  initialsOf as sharedInitialsOf,
  useAdminProfile,
  useUpdateAdminProfile,
  useUploadAvatar,
} from "@/hooks/useAdminProfile";
import { useAdminUsers, useCreateAdmin, useRemoveAdmin } from "@/hooks/useAdminUsers";
import { BillingSettingsTab } from "./BillingSettingsTab";
import { ApiDocs } from "./ApiDocs";
import { LeadRejectionsTab } from "./LeadRejectionsTab";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

type SettingsTab = "general" | "security" | "admins" | "signature" | "billing" | "spam" | "api";

function resolveSettingsTab(param: string | null): SettingsTab {
  if (
    param === "general" ||
    param === "security" ||
    param === "admins" ||
    param === "signature" ||
    param === "billing" ||
    param === "spam" ||
    param === "api"
  ) {
    return param;
  }
  return "general";
}

function initialsOf(name: string | null | undefined, fallback: string) {
  return sharedInitialsOf(name || fallback.split("@")[0], "?");
}

const sectionCls =
  "bg-card border border-border rounded-2xl shadow-card p-[26px] flex flex-col gap-[18px]";
const labelCls = "text-xs font-semibold text-muted-foreground";
const inputCls = "h-10 rounded-[10px] bg-muted/60 text-[13.5px] px-[13px]";
const primaryBtn = "h-[38px] px-[18px] rounded-[10px] text-[13px] font-semibold";
const outlineBtn = "h-[38px] px-4 rounded-[10px] text-[13px] font-semibold";
const segTrigger =
  "rounded-lg px-4 py-2 text-[12.5px] font-semibold text-muted-foreground data-[state=active]:bg-card data-[state=active]:text-foreground data-[state=active]:shadow-sm";
const segList = "h-auto w-fit flex-wrap justify-start gap-[3px] p-[3px] rounded-[10px] bg-muted";

const AVATAR_MAX_BYTES = 2 * 1024 * 1024;

export function Settings() {
  const { user, updatePassword } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = resolveSettingsTab(searchParams.get("tab"));
  const handleTabChange = (value: string) => {
    const tab = resolveSettingsTab(value);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("tab", tab);
      return next;
    });
  };
  const { data: profile } = useAdminProfile();
  const updateProfile = useUpdateAdminProfile();
  const uploadAvatar = useUploadAvatar();
  const { data: adminUsers, isLoading: adminsLoading } = useAdminUsers();
  const createAdmin = useCreateAdmin();
  const removeAdmin = useRemoveAdmin();
  const saving = updateProfile.isPending;
  const uploadingAvatar = uploadAvatar.isPending;
  const addingAdmin = createAdmin.isPending;
  const [changingPassword, setChangingPassword] = useState(false);
  const avatarInputRef = useRef<HTMLInputElement>(null);

  const [profileName, setProfileName] = useState("");
  const [profilePhone, setProfilePhone] = useState("");
  const [profileBio, setProfileBio] = useState("");
  const [profileAvatar, setProfileAvatar] = useState("");
  const [profileSnapshot, setProfileSnapshot] = useState({ name: "", phone: "", bio: "" });

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // Admin management
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState("");
  const [newAdminPassword, setNewAdminPassword] = useState("");
  const [newAdminName, setNewAdminName] = useState("");

  // Hidrata o formulário quando o perfil carrega (query já cacheada pela sidebar).
  useEffect(() => {
    if (!profile) return;
    const snap = { name: profile.name || "", phone: profile.phone || "", bio: profile.bio || "" };
    setProfileName(snap.name);
    setProfilePhone(snap.phone);
    setProfileBio(snap.bio);
    setProfileSnapshot(snap);
    setProfileAvatar(profile.avatar_url || "");
  }, [profile]);

  const handleSaveProfile = async () => {
    if (!user) return;
    try {
      await updateProfile.mutateAsync({
        name: profileName.trim() || null,
        phone: profilePhone.trim() || null,
        bio: profileBio.trim() || null,
      });
      setProfileSnapshot({ name: profileName, phone: profilePhone, bio: profileBio });
      toast.success("Perfil atualizado com sucesso!");
    } catch {
      toast.error("Erro ao salvar perfil");
    }
  };

  const handleCancelProfile = () => {
    setProfileName(profileSnapshot.name);
    setProfilePhone(profileSnapshot.phone);
    setProfileBio(profileSnapshot.bio);
  };

  const handleAvatarFile = async (file: File) => {
    if (!user) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Selecione um arquivo de imagem");
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      toast.error("Imagem muito grande (máx. 2 MB)");
      return;
    }
    try {
      const url = await uploadAvatar.mutateAsync(file);
      setProfileAvatar(url);
      toast.success("Foto atualizada!");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao enviar foto");
    } finally {
      if (avatarInputRef.current) avatarInputRef.current.value = "";
    }
  };

  const handleChangePassword = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error("Preencha todos os campos de senha");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("As senhas não coincidem");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres");
      return;
    }
    setChangingPassword(true);
    try {
      const { error } = await updatePassword(newPassword);
      if (error) throw error;
      toast.success("Senha alterada com sucesso!");
      setNewPassword("");
      setConfirmPassword("");
    } catch {
      toast.error("Erro ao alterar senha");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleAddAdmin = async () => {
    if (!newAdminEmail || !newAdminPassword) {
      toast.error("Email e senha são obrigatórios");
      return;
    }
    try {
      await createAdmin.mutateAsync({
        email: newAdminEmail,
        password: newAdminPassword,
        name: newAdminName || undefined,
      });
      toast.success("Administrador criado com sucesso!");
      setShowAddAdmin(false);
      setNewAdminEmail("");
      setNewAdminPassword("");
      setNewAdminName("");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao criar administrador");
    }
  };

  const handleRemoveAdmin = async (userId: string) => {
    try {
      await removeAdmin.mutateAsync(userId);
      toast.success("Administrador removido!");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao remover administrador");
    }
  };

  const email = user?.email || "";
  const lastSignIn = user?.last_sign_in_at
    ? format(new Date(user.last_sign_in_at), "EEEE, d 'de' MMMM 'às' HH:mm", { locale: ptBR })
    : null;

  return (
    <div className="mx-auto flex max-w-[900px] flex-col gap-[22px]">
      <div>
        <h1 className="m-0 mb-1.5 text-[27px] font-bold tracking-[-0.03em]">Configurações</h1>
        <p className="m-0 text-[13.5px] text-muted-foreground">
          Perfil, assinatura digital, cobrança e acessos
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="flex flex-col gap-[22px]">
        <TabsList className={segList}>
          <TabsTrigger value="general" className={segTrigger}>
            Perfil
          </TabsTrigger>
          <TabsTrigger value="security" className={segTrigger}>
            Segurança
          </TabsTrigger>
          <TabsTrigger value="admins" className={segTrigger}>
            Administradores
          </TabsTrigger>
          <TabsTrigger value="signature" className={segTrigger}>
            Assinatura
          </TabsTrigger>
          <TabsTrigger value="billing" className={segTrigger}>
            Cobrança
          </TabsTrigger>
          <TabsTrigger value="spam" className={segTrigger}>
            Anti-spam
          </TabsTrigger>
          <TabsTrigger value="api" className={segTrigger}>
            API
          </TabsTrigger>
        </TabsList>

        {/* Perfil */}
        <TabsContent value="general" className="mt-0">
          <section className={`${sectionCls} gap-[22px]`}>
            <div className="flex items-center gap-[18px] border-b border-border/70 pb-[22px]">
              {profileAvatar ? (
                <img
                  src={profileAvatar}
                  alt=""
                  className="h-[60px] w-[60px] shrink-0 rounded-[14px] object-cover"
                />
              ) : (
                <div className="flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-[14px] bg-accent text-lg font-bold text-primary">
                  {initialsOf(profileName, email)}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-[15px] font-bold">{profileName || "Sem nome"}</div>
                <div className="truncate text-[12.5px] text-muted-foreground">{email}</div>
              </div>
              <input
                ref={avatarInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleAvatarFile(f);
                }}
              />
              <Button
                variant="outline"
                className="h-[34px] rounded-[9px] px-3.5 text-[12.5px] font-semibold"
                onClick={() => avatarInputRef.current?.click()}
                disabled={uploadingAvatar}
              >
                {uploadingAvatar && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                Trocar foto
              </Button>
            </div>

            <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-[18px]">
              <div className="flex flex-col gap-[7px]">
                <label htmlFor="profile-name" className={labelCls}>
                  Nome
                </label>
                <Input
                  id="profile-name"
                  className={inputCls}
                  value={profileName}
                  onChange={(e) => setProfileName(e.target.value)}
                  placeholder="Seu nome"
                />
              </div>
              <div className="flex flex-col gap-[7px]">
                <label htmlFor="profile-phone" className={labelCls}>
                  Telefone
                </label>
                <Input
                  id="profile-phone"
                  className={inputCls}
                  value={profilePhone}
                  onChange={(e) => setProfilePhone(e.target.value)}
                  placeholder="(00) 00000-0000"
                />
              </div>
              <div className="col-span-full flex flex-col gap-[7px]">
                <label htmlFor="profile-bio" className={labelCls}>
                  Bio pública
                </label>
                <Textarea
                  id="profile-bio"
                  className="min-h-[80px] rounded-[10px] bg-muted/60 px-[13px] py-3 text-[13.5px] leading-[1.55]"
                  value={profileBio}
                  onChange={(e) => setProfileBio(e.target.value)}
                  placeholder="Desenvolvedor e consultor de automação empresarial. Ajudo empresas a integrar CRM, ERP e atendimento em um só fluxo."
                />
              </div>
            </div>

            <div className="flex justify-end gap-[9px]">
              <Button
                variant="outline"
                className={outlineBtn}
                onClick={handleCancelProfile}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button className={primaryBtn} onClick={handleSaveProfile} disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Salvar alterações
              </Button>
            </div>
          </section>
        </TabsContent>

        {/* Segurança */}
        <TabsContent value="security" className="mt-0">
          <section className={sectionCls}>
            <h2 className="m-0 text-[15px] font-bold">Alterar senha</h2>
            <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-[18px]">
              <div className="flex flex-col gap-[7px]">
                <label htmlFor="new-password" className={labelCls}>
                  Nova senha
                </label>
                <Input
                  id="new-password"
                  className={inputCls}
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                />
              </div>
              <div className="flex flex-col gap-[7px]">
                <label htmlFor="confirm-password" className={labelCls}>
                  Confirmar senha
                </label>
                <Input
                  id="confirm-password"
                  className={inputCls}
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirme a nova senha"
                />
              </div>
            </div>
            {lastSignIn && (
              <div className="rounded-xl bg-accent px-[17px] py-[15px] text-[12.5px] font-semibold leading-normal text-primary">
                Última entrada: {lastSignIn}.
              </div>
            )}
            <div className="flex justify-end">
              <Button
                className={primaryBtn}
                onClick={handleChangePassword}
                disabled={changingPassword}
              >
                {changingPassword && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Atualizar senha
              </Button>
            </div>
          </section>
        </TabsContent>

        {/* Administradores */}
        <TabsContent value="admins" className="mt-0">
          <section className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
            <div className="flex items-center justify-between border-b border-border/70 px-6 pb-3.5 pt-[18px]">
              <h2 className="m-0 text-[15px] font-bold">Administradores</h2>
              <Button
                className="h-8 rounded-lg px-[13px] text-xs font-semibold"
                onClick={() => setShowAddAdmin(true)}
              >
                Adicionar
              </Button>
            </div>
            {adminsLoading ? (
              <div className="py-6 text-center text-sm text-muted-foreground">Carregando...</div>
            ) : !adminUsers || adminUsers.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Nenhum administrador encontrado.
              </div>
            ) : (
              adminUsers.map((admin) => (
                <div
                  key={admin.user_id}
                  className="flex items-center gap-4 border-b border-border/70 px-6 py-4 last:border-b-0 hover:bg-muted/40"
                >
                  {admin.avatar_url ? (
                    <img
                      src={admin.avatar_url}
                      alt=""
                      className="h-9 w-9 shrink-0 rounded-full object-cover"
                    />
                  ) : (
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent text-xs font-bold text-primary">
                      {initialsOf(admin.name, admin.email)}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-[13.5px] font-semibold">
                      {admin.name}
                      {admin.user_id === user?.id && (
                        <span className="font-normal text-muted-foreground"> · você</span>
                      )}
                    </div>
                    <div className="truncate text-[11.5px] text-muted-foreground">
                      {admin.email}
                    </div>
                  </div>
                  <span className="inline-flex h-[23px] items-center rounded-[7px] bg-muted px-2.5 text-[11px] font-bold text-muted-foreground">
                    Admin
                  </span>
                  <span className="hidden text-[11.5px] text-muted-foreground sm:inline">
                    {admin.created_at
                      ? `desde ${format(new Date(admin.created_at), "dd/MM/yyyy")}`
                      : ""}
                  </span>
                  {admin.user_id !== user?.id ? (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-bad"
                          aria-label="Remover administrador"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Remover administrador?</AlertDialogTitle>
                          <AlertDialogDescription>
                            {admin.name} perderá acesso ao painel administrativo.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancelar</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleRemoveAdmin(admin.user_id)}
                            className="bg-bad text-bad-foreground hover:bg-bad/90"
                          >
                            Remover
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  ) : (
                    <span className="w-8" />
                  )}
                </div>
              ))
            )}
          </section>
        </TabsContent>

        {/* Assinatura */}
        <TabsContent value="signature" className="mt-0">
          <AdminSignatureTab />
        </TabsContent>

        {/* Cobrança */}
        <TabsContent value="billing" className="mt-0">
          <BillingSettingsTab />
        </TabsContent>

        {/* API */}
        <TabsContent value="spam" className="mt-0">
          <LeadRejectionsTab />
        </TabsContent>

        <TabsContent value="api" className="mt-0">
          <ApiDocs />
        </TabsContent>
      </Tabs>

      {/* Add Admin Dialog */}
      <Dialog open={showAddAdmin} onOpenChange={setShowAddAdmin}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adicionar administrador</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex flex-col gap-[7px]">
              <label htmlFor="new-admin-name" className={labelCls}>
                Nome
              </label>
              <Input
                id="new-admin-name"
                className={inputCls}
                value={newAdminName}
                onChange={(e) => setNewAdminName(e.target.value)}
                placeholder="Nome do administrador"
              />
            </div>
            <div className="flex flex-col gap-[7px]">
              <label htmlFor="new-admin-email" className={labelCls}>
                E-mail *
              </label>
              <Input
                id="new-admin-email"
                className={inputCls}
                type="email"
                value={newAdminEmail}
                onChange={(e) => setNewAdminEmail(e.target.value)}
                placeholder="admin@email.com"
              />
            </div>
            <div className="flex flex-col gap-[7px]">
              <label htmlFor="new-admin-password" className={labelCls}>
                Senha *
              </label>
              <Input
                id="new-admin-password"
                className={inputCls}
                type="password"
                value={newAdminPassword}
                onChange={(e) => setNewAdminPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className={outlineBtn} onClick={() => setShowAddAdmin(false)}>
              Cancelar
            </Button>
            <Button className={primaryBtn} onClick={handleAddAdmin} disabled={addingAdmin}>
              {addingAdmin && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Adicionar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── AdminSignatureTab ────────────────────────────────────────────────────────

function AdminSignatureTab() {
  const { data: saved, isLoading } = useAdminSignature();
  const save = useSaveAdminSignature();
  const del = useDeleteAdminSignature();

  const [tab, setTab] = useState<"drawn" | "typed" | "certificate">("drawn");
  const sigPadRef = useRef<SignatureCanvas>(null);
  const certInputRef = useRef<HTMLInputElement>(null);
  const [typedName, setTypedName] = useState("");
  const [certFile, setCertFile] = useState<File | null>(null);
  const [certPassword, setCertPassword] = useState("");
  const [certInfo, setCertInfo] = useState<Record<string, string> | null>(null);
  const [certData, setCertData] = useState("");
  const [hasStroke, setHasStroke] = useState(false);

  const handleSave = async () => {
    if (tab === "drawn") {
      if (!sigPadRef.current || sigPadRef.current.isEmpty()) {
        toast.error("Desenhe sua assinatura antes de salvar");
        return;
      }
      const data = sigPadRef.current.getCanvas().toDataURL("image/png");
      await save.mutateAsync({ signature_type: "drawn", signature_data: data });
    } else if (tab === "typed") {
      if (!typedName.trim()) {
        toast.error("Digite seu nome");
        return;
      }
      await save.mutateAsync({ signature_type: "typed", signature_data: typedName.trim() });
    } else {
      if (!certData) {
        toast.error("Carregue um certificado válido");
        return;
      }
      await save.mutateAsync({
        signature_type: "certificate",
        signature_data: certData,
        certificate_info: certInfo,
      });
    }
  };

  const handleClear = () => {
    if (tab === "drawn") {
      sigPadRef.current?.clear();
      setHasStroke(false);
    } else if (tab === "typed") {
      setTypedName("");
    } else {
      setCertFile(null);
      setCertPassword("");
      setCertInfo(null);
      setCertData("");
      if (certInputRef.current) certInputRef.current.value = "";
    }
  };

  const handleCertLoad = async (file: File, password: string) => {
    try {
      const forge = await import("node-forge");
      const arrayBuffer = await file.arrayBuffer();
      const bytes = String.fromCharCode(...new Uint8Array(arrayBuffer));
      const p12 = forge.pkcs12.pkcs12FromAsn1(forge.asn1.fromDer(bytes), password);
      const certBags = p12.getBags({ bagType: forge.pki.oids.certBag });
      const cert = Object.values(certBags)[0]?.[0]?.cert;
      if (!cert) throw new Error("Certificado não encontrado no arquivo");
      const info = {
        cn: cert.subject.getField("CN")?.value ?? "",
        issuer: cert.issuer.getField("CN")?.value ?? "",
        validFrom: cert.validity.notBefore.toISOString(),
        validTo: cert.validity.notAfter.toISOString(),
      };
      setCertInfo(info);
      setCertData(forge.pki.certificateToPem(cert));
      toast.success("Certificado carregado: " + info.cn);
    } catch (e) {
      toast.error("Erro ao ler certificado: " + (e as Error).message);
    }
  };

  const typeLabel: Record<string, string> = {
    drawn: "desenhada",
    typed: "digitada",
    certificate: "certificado digital",
  };

  if (isLoading)
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );

  const areaCls =
    "h-[180px] border border-dashed border-border rounded-xl bg-muted/60 overflow-hidden relative";

  return (
    <section className={sectionCls}>
      <h2 className="m-0 text-[15px] font-bold">Minha assinatura digital</h2>

      {saved && (
        <div className="flex items-start justify-between gap-3 rounded-xl bg-ok-soft px-[17px] py-[13px]">
          <div className="min-w-0">
            <p className="m-0 text-[13px] font-semibold text-ok">
              Assinatura cadastrada ({typeLabel[saved.signature_type]})
            </p>
            {saved.signature_type === "drawn" && (
              <img
                src={saved.signature_data}
                alt="Assinatura"
                className="mt-2 h-12 rounded border border-border bg-card"
              />
            )}
            {saved.signature_type === "typed" && (
              <p className="m-0 mt-1 font-display text-[22px]">{saved.signature_data}</p>
            )}
            {saved.signature_type === "certificate" && saved.certificate_info && (
              <p className="m-0 mt-1 text-[11.5px] text-ok">
                {saved.certificate_info.cn} · válido até{" "}
                {new Date(saved.certificate_info.validTo).toLocaleDateString("pt-BR")}
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => del.mutate()}
            disabled={del.isPending}
            className="h-8 w-8 shrink-0 text-bad"
            aria-label="Remover assinatura"
            title="Remover assinatura"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )}

      <Tabs
        value={tab}
        onValueChange={(v) => setTab(v as typeof tab)}
        className="flex flex-col gap-[18px]"
      >
        <TabsList className={segList}>
          <TabsTrigger value="drawn" className={segTrigger}>
            Desenhar
          </TabsTrigger>
          <TabsTrigger value="typed" className={segTrigger}>
            Digitar
          </TabsTrigger>
          <TabsTrigger value="certificate" className={segTrigger}>
            Certificado A1
          </TabsTrigger>
        </TabsList>

        <TabsContent value="drawn" className="mt-0">
          <div className={areaCls}>
            {!hasStroke && (
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center font-mono text-[11px] text-muted-foreground">
                desenhe sua assinatura aqui com o dedo ou o mouse
              </span>
            )}
            <SignatureCanvas
              ref={sigPadRef}
              canvasProps={{ className: "w-full h-full" }}
              penColor="#111"
              onBegin={() => setHasStroke(true)}
            />
          </div>
        </TabsContent>

        <TabsContent value="typed" className="mt-0">
          <div className={`${areaCls} flex items-center justify-center px-6`}>
            <input
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder="Digite seu nome completo"
              className="w-full bg-transparent text-center font-display text-[30px] outline-none placeholder:font-sans placeholder:text-[13px] placeholder:text-muted-foreground"
            />
          </div>
        </TabsContent>

        <TabsContent value="certificate" className="mt-0 flex flex-col gap-3">
          <input
            ref={certInputRef}
            type="file"
            accept=".p12,.pfx"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) setCertFile(f);
            }}
          />
          <button
            type="button"
            onClick={() => certInputRef.current?.click()}
            className={`${areaCls} flex w-full flex-col items-center justify-center gap-1 text-center transition-colors hover:border-primary`}
          >
            <span className="text-[13px] font-semibold">
              {certFile?.name ?? "Selecione seu certificado .p12 ou .pfx"}
            </span>
            <span className="text-[11.5px] text-muted-foreground">
              O arquivo é lido no seu navegador e não é enviado
            </span>
          </button>
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex min-w-[200px] flex-1 flex-col gap-[7px]">
              <label htmlFor="cert-password" className={labelCls}>
                Senha do certificado
              </label>
              <Input
                id="cert-password"
                className={inputCls}
                type="password"
                value={certPassword}
                onChange={(e) => setCertPassword(e.target.value)}
                placeholder="••••••••"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              className="h-10 rounded-[10px] px-4 text-[13px] font-semibold"
              disabled={!certFile}
              onClick={() => certFile && handleCertLoad(certFile, certPassword)}
            >
              Carregar certificado
            </Button>
          </div>
          {certInfo && (
            <div className="space-y-0.5 rounded-[10px] border border-border bg-muted/60 px-[13px] py-2.5 text-[12px]">
              <p className="m-0">
                <strong>CN:</strong> {certInfo.cn}
              </p>
              <p className="m-0">
                <strong>Emissor:</strong> {certInfo.issuer}
              </p>
              <p className="m-0">
                <strong>Válido até:</strong>{" "}
                {new Date(certInfo.validTo).toLocaleDateString("pt-BR")}
              </p>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground">
          Usada em orçamentos e contratos enviados para assinatura eletrônica.
        </span>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            className="h-9 rounded-[9px] px-3.5 text-[12.5px] font-semibold"
            onClick={handleClear}
          >
            Limpar
          </Button>
          <Button
            type="button"
            className="h-9 rounded-[9px] px-4 text-[12.5px] font-semibold"
            onClick={handleSave}
            disabled={save.isPending}
          >
            {save.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Salvar assinatura
          </Button>
        </div>
      </div>
    </section>
  );
}
